import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { useTheme } from "next-themes";

const LoadingOverlay = () => {
  const router = useRouter();
  const { resolvedTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    const handleStart = () => {
      setLoading(true);
      setProgress(0);

      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Simulate progress animation with more realistic progression
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 85) {
            return prev + Math.random() * 2; // Slow down near the end
          } else if (prev >= 70) {
            return prev + Math.random() * 5;
          } else if (prev >= 50) {
            return prev + Math.random() * 8;
          } else {
            return prev + Math.random() * 15; // Fast start
          }
        });
      }, 150);
    };

    const handleComplete = () => {
      // Clear the interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Complete the progress bar
      setProgress(100);

      // Hide the bar after animation completes
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 400);
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);

      // Clean up interval on unmount
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [router]);

  // Define colors based on theme and progress
  const getProgressColors = () => {
    const isDark = resolvedTheme === "dark";

    if (isDark) {
      // Dark mode: Use the purple accent (#8358FF) with your logo colors
      return {
        background: "linear-gradient(90deg, #8358FF, #8C308A, #F28212)",
        shadow: "0 0 8px rgba(131, 88, 255, 0.4)",
      };
    } else {
      // Light mode: Use the blue accent (#4E6EB6) with your logo colors
      return {
        background: "linear-gradient(90deg, #4E6EB6, #104F9B, #8C308A)",
        shadow: "0 0 8px rgba(78, 110, 182, 0.4)",
      };
    }
  };

  if (!loading) return null;

  const colors = getProgressColors();

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-1">
      <div className="h-full bg-jacarta-100 dark:bg-jacarta-700">
        <div
          className="h-full transition-all duration-300 ease-out"
          style={{
            width: `${Math.min(progress, 100)}%`,
            background: colors.background,
            boxShadow: colors.shadow,
          }}
        />
      </div>
    </div>
  );
};

export default LoadingOverlay;
